// hooks/useOnlineStatus.js
import { useEffect, useState, useRef } from "react";
import { useSession } from "@clerk/clerk-react";
import { storeUserOnlineStatus } from "../api/authenticationAPI";

// How long to wait before marking user offline when app is backgrounded (ms)
const BACKGROUND_OFFLINE_DELAY = 30000; // 30 seconds
// Heartbeat interval - server will mark offline if no heartbeat received
const HEARTBEAT_INTERVAL = 60000; // 60 seconds

export const useOnlineStatus = (userId, chatClient) => {
  const { isLoaded, session, isSignedIn } = useSession();
  const [isOnline, setIsOnline] = useState(false);
  const hasInitialized = useRef(false);
  const backgroundTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const lastHeartbeatRef = useRef(Date.now());

  useEffect(() => {
    // Check required dependencies first
    if (!userId || !isSignedIn || !isLoaded || !session || !chatClient) {
      return;
    }
    // Only initialize once all dependencies are ready
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const backendOrigin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : process.env.REACT_APP_BACKEND_URL || "";
    const onlineStatusEndpoint = `${backendOrigin}/api/auth/store-user-online-status`;

    // Send offline beacon - used for page close events
    const sendOfflineBeacon = () => {
      if (navigator.sendBeacon) {
        const data = JSON.stringify({ userId, isOnline: false });
        const blob = new Blob([data], { type: "application/json" });
        navigator.sendBeacon(onlineStatusEndpoint, blob);
      }
    };

    const updateStatus = async (online) => {
      setIsOnline(online);
      lastHeartbeatRef.current = Date.now();

      // Always try to update backend status
      if (navigator.onLine || !online) {
        try {
          await storeUserOnlineStatus(userId, online);
        } catch (error) {
          if (error.code !== "ERR_NETWORK") {
            console.error("Failed to update online status", error);
          }
        }
      }
    };

    // Mark user online on mount (Stream Chat connection already established)
    updateStatus(true);

    // Heartbeat to keep server updated - handles cases where all other events fail
    heartbeatIntervalRef.current = setInterval(() => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        storeUserOnlineStatus(userId, true).catch(() => {
          // Silently ignore heartbeat failures
        });
        lastHeartbeatRef.current = Date.now();
      }
    }, HEARTBEAT_INTERVAL);

    // Listen to Stream Chat connection state changes - handles tab close/disconnect
    const handleConnectionChange = (event) => {
      const isConnected = event.online;
      updateStatus(isConnected);
    };

    chatClient.on("connection.changed", handleConnectionChange);

    // Handle browser online/offline events as fallback
    const handleOnline = () => {
      // Clear any pending offline timeout
      if (backgroundTimeoutRef.current) {
        clearTimeout(backgroundTimeoutRef.current);
        backgroundTimeoutRef.current = null;
      }
      updateStatus(true);
    };
    const handleOffline = () => updateStatus(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Handle beforeunload to mark offline when tab closes (desktop)
    const handleBeforeUnload = () => {
      sendOfflineBeacon();
    };

    // Handle pagehide - more reliable on mobile PWAs than beforeunload
    // This fires when the page is being navigated away from or app is closing
    const handlePageHide = (event) => {
      // event.persisted is true if page might be restored from bfcache
      // We still want to mark offline since mobile might terminate the app
      sendOfflineBeacon();
    };

    // Handle visibility change - for mobile PWA background detection
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // App went to background - start countdown to mark offline
        // This handles PWA being minimized/switched away
        backgroundTimeoutRef.current = setTimeout(() => {
          // If still hidden after delay, mark offline via beacon
          if (document.visibilityState === "hidden") {
            sendOfflineBeacon();
          }
        }, BACKGROUND_OFFLINE_DELAY);
      } else if (document.visibilityState === "visible") {
        // App came back to foreground - cancel offline countdown
        if (backgroundTimeoutRef.current) {
          clearTimeout(backgroundTimeoutRef.current);
          backgroundTimeoutRef.current = null;
        }
        // Mark online again since we're back
        updateStatus(true);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      // DON'T mark offline on cleanup - this fires during page navigation in SPA
      // Offline status is handled by:
      // 1. beforeunload event (tab/browser close)
      // 2. pagehide event (mobile PWA close)
      // 3. visibilitychange with timeout (mobile PWA background)
      // 4. connection.changed event (Stream Chat disconnect)
      // 5. browser offline event (network loss)
      // 6. Server-side heartbeat timeout (fallback)
      chatClient.off("connection.changed", handleConnectionChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (backgroundTimeoutRef.current) {
        clearTimeout(backgroundTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [userId, isSignedIn, isLoaded, session, chatClient]);

  return isOnline;
};
